import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ThankYou() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://curtiswaters.ai';

    // Set page title
    document.title = 'Thank You - Curtis Waters AI';

    // Set meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    setMetaTag('description', 'Thank you for contacting Curtis Waters AI. Your message has been received and we\'ll respond shortly to discuss your AI business intelligence needs.');
    setMetaTag('keywords', 'thank you, contact confirmation, Curtis Waters AI, AI solutions');
    setMetaTag('robots', 'noindex, nofollow');
    setMetaTag('og:title', 'Thank You - Curtis Waters AI', true);
    setMetaTag('og:description', 'Thank you for contacting Curtis Waters AI. We\'ll respond shortly to discuss your needs.', true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', `${siteUrl}/thank-you`, true);
    setMetaTag('twitter:card', 'summary');
    setMetaTag('twitter:title', 'Thank You - Curtis Waters AI');
    setMetaTag('twitter:description', 'Thank you for contacting Curtis Waters AI. We\'ll respond shortly.');

    // Set canonical URL
    let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      document.head.appendChild(linkElement);
    }
    linkElement.setAttribute('href', `${siteUrl}/thank-you`);

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Thank You - Curtis Waters AI",
      "description": "Thank you for contacting Curtis Waters AI. Your message has been received.",
      "url": `${siteUrl}/thank-you`
    };

    const existingScript = document.querySelector('script[data-schema="thank-you"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'thank-you');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="thank-you"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow flex items-center justify-center px-6 py-24">
        <div className="max-w-2xl w-full text-center">
          <div className="w-24 h-24 mx-auto mb-8 bg-teal-500/10 rounded-full flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-5xl text-teal-500"></i>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Thank You for Reaching Out!
          </h1>
          
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Your message has been successfully received. Curtis Waters will review your inquiry and respond within 24 hours to discuss your <strong>AI business intelligence needs</strong> and <strong>deal evaluation</strong> requirements.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-8 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">What Happens Next?</h2>
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">1</span>
                </div>
                <p className="text-gray-700 pt-1">We'll review your AI solutions requirements and business needs</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">2</span>
                </div>
                <p className="text-gray-700 pt-1">Curtis will personally reach out to discuss customized AI business intelligence solutions</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">3</span>
                </div>
                <p className="text-gray-700 pt-1">We'll schedule a consultation to assess your needs and recommend the best AI solutions</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              Return to Homepage
            </a>
            <a 
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-gray-100 text-gray-900 font-medium rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              Contact Information
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
